/**
 * useIdleDetector
 *
 * 一定時間ユーザー操作がない場合にコールバックを発火するフック。
 * mousemove / keydown / click / scroll / touchstart を監視。
 *
 * BroadcastChannel により同一ブラウザの他タブ/他ウィンドウでの操作も共有する。
 * 例: タブA を操作中にタブB のアイドルタイマーも自動リセットされる。
 */
import { useEffect, useRef } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'wheel', 'touchstart', 'touchmove'] as const;
const ACTIVITY_CHANNEL = 'user-activity-sync';
const BROADCAST_THROTTLE_MS = 2000; // 2秒に1回だけブロードキャスト

export function useIdleDetector(
  timeoutMs: number,
  onIdle: () => void,
  enabled: boolean = true,
): void {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastBroadcast = 0;

    const channel: BroadcastChannel | null =
      typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(ACTIVITY_CHANNEL) : null;

    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        onIdleRef.current();
      }, timeoutMs);
    };

    // 自タブでの操作: タイマーリセット + 他タブへブロードキャスト（スロットル付き）
    const handleLocalActivity = () => {
      resetTimer();
      const now = Date.now();
      if (channel && now - lastBroadcast >= BROADCAST_THROTTLE_MS) {
        lastBroadcast = now;
        try {
          channel.postMessage({ type: 'activity', ts: now });
        } catch { /* ignore */ }
      }
    };

    // 他タブからの操作通知: タイマーだけリセット（再ブロードキャストはしない）
    const handleRemoteMessage = (e: MessageEvent) => {
      if (e.data?.type === 'activity') {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          onIdleRef.current();
        }, timeoutMs);
      }
    };

    // 初回タイマー開始
    resetTimer();

    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, handleLocalActivity, { passive: true });
    }
    // scroll はバブリングしないので capture フェーズで拾う
    // （overflow: auto 内部のスクロールも検知するため）
    document.addEventListener('scroll', handleLocalActivity, { passive: true, capture: true });
    channel?.addEventListener('message', handleRemoteMessage);

    return () => {
      if (timer) clearTimeout(timer);
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, handleLocalActivity);
      }
      document.removeEventListener('scroll', handleLocalActivity, { capture: true } as any);
      channel?.removeEventListener('message', handleRemoteMessage);
      channel?.close();
    };
  }, [timeoutMs, enabled]);
}
